import React, { useEffect, useState } from "react";
import {
  getSchedulingSettings,
  connectCalcom,
  disconnectCalcom,
  listCompanyInterviews,
  patchInterview,
  inspectCalcom,
  finalizeCalcom,
} from "@/lib/schedulingApi";
import {
  CalendarDays,
  Loader2,
  CheckCircle2,
  Link2,
  X,
  Video,
  Mic,
  MapPin,
  ExternalLink,
  Copy,
  AlertCircle,
  Clock,
} from "lucide-react";

const MODE_ICON = { video: Video, voice: Mic, onsite: MapPin };

const STATUS_STYLE = {
  invited: "bg-amber-50 border-amber-200 text-amber-700",
  booked: "bg-emerald-50 border-emerald-200 text-emerald-700",
  rescheduled: "bg-blue-50 border-blue-200 text-blue-700",
  cancelled: "bg-stone-100 border-stone-200 text-stone-500",
  completed: "bg-stone-900 border-stone-900 text-white",
  no_show: "bg-red-50 border-red-200 text-red-700",
};

const STATUS_LABEL = {
  invited: "Invited",
  booked: "Booked",
  rescheduled: "Rescheduled",
  cancelled: "Cancelled",
  completed: "Completed",
  no_show: "No-show",
};

export default function Schedule() {
  const [settings, setSettings] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, iv] = await Promise.all([
        getSchedulingSettings(),
        listCompanyInterviews().catch(() => ({ data: [] })),
      ]);
      setSettings(s.data);
      setInterviews(iv.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleStatus = async (interviewId, status) => {
    await patchInterview(interviewId, { status });
    setInterviews((arr) =>
      arr.map((i) => (i.interview_id === interviewId ? { ...i, status } : i))
    );
  };

  return (
    <div className="container-x py-10 md:py-14">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <span className="overline">Interview scheduling</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
            Book interviews{" "}
            <span className="italic text-stone-500">without the email ping-pong.</span>
          </h1>
          <p className="mt-3 text-stone-600 max-w-xl">
            Connect Cal.com once. Invite any candidate with one click — they pick a slot,
            everyone gets a calendar invite and video link automatically.
          </p>
        </div>
        {settings?.connected ? (
          <button onClick={() => setShowConnect(true)} className="btn-ghost text-sm">
            <Link2 size={14} /> Cal.com settings
          </button>
        ) : (
          <button onClick={() => setShowConnect(true)} className="btn-primary">
            <Link2 size={16} /> Connect Cal.com
          </button>
        )}
      </div>

      {/* Connection status banner */}
      {!loading && (
        settings?.connected ? (
          <div className="mb-8 rounded-3xl bg-emerald-50 border border-emerald-200 p-5 flex items-center gap-4">
            <CheckCircle2 size={20} className="text-emerald-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-emerald-800">Cal.com connected</p>
              <p className="text-sm text-emerald-700 truncate">
                Booking link: <code className="font-mono">cal.com/{settings.cal_link}</code>
              </p>
            </div>
          </div>
        ) : (
          <div className="mb-8 rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-6 flex items-start gap-4">
            <AlertCircle size={20} className="text-stone-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-stone-700">Connect Cal.com to start scheduling</p>
              <p className="text-sm text-stone-500 mt-1">
                It's free. Create an event type on cal.com (e.g. a 30-min interview), then paste
                your link here.
              </p>
              <button onClick={() => setShowConnect(true)} className="btn-primary mt-4 text-sm">
                <Link2 size={14} /> Connect now
              </button>
            </div>
          </div>
        )
      )}

      {/* Interviews list */}
      <h2 className="font-display text-2xl mb-5">Interviews</h2>
      {loading ? (
        <p className="text-stone-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : interviews.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-10 text-center text-stone-600">
          No interviews yet. Open any candidate's profile and click{" "}
          <strong>Schedule interview</strong> to send a booking invite.
        </div>
      ) : (
        <div className="space-y-3">
          {interviews.map((iv) => {
            const Icon = MODE_ICON[iv.mode] || Video;
            return (
              <div
                key={iv.interview_id}
                className="rounded-2xl bg-white border border-stone-200 p-5 flex flex-col md:flex-row md:items-center gap-4"
              >
                <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center shrink-0">
                  <Icon size={18} className="text-stone-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-stone-800">{iv.candidate_name || iv.candidate_email}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-wide ${STATUS_STYLE[iv.status]}`}>
                      {STATUS_LABEL[iv.status]}
                    </span>
                  </div>
                  <p className="text-sm text-stone-500 mt-0.5">
                    {iv.job_title || "Interview"} · {iv.duration_min}min {iv.mode}
                  </p>
                  {iv.scheduled_at && (
                    <p className="text-sm text-stone-600 mt-1 inline-flex items-center gap-1.5">
                      <Clock size={13} className="text-stone-400" />
                      {new Date(iv.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {iv.meeting_url && (
                    <a
                      href={iv.meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C2410C] text-white hover:bg-orange-700 text-xs font-medium transition-colors"
                    >
                      Join <ExternalLink size={11} />
                    </a>
                  )}
                  {iv.status === "booked" && (
                    <>
                      <button
                        onClick={() => handleStatus(iv.interview_id, "completed")}
                        className="px-3 py-1.5 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50 text-xs transition-colors"
                      >
                        Mark done
                      </button>
                      <button
                        onClick={() => handleStatus(iv.interview_id, "no_show")}
                        className="px-3 py-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-xs transition-colors"
                      >
                        No-show
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showConnect && (
        <ConnectModal
          settings={settings}
          onClose={() => setShowConnect(false)}
          onSaved={() => { setShowConnect(false); load(); }}
          onDisconnect={async () => { await disconnectCalcom(); setShowConnect(false); load(); }}
        />
      )}
    </div>
  );
}

function ConnectModal({ settings, onClose, onSaved, onDisconnect }) {
  // step: "choose" → pick auto vs manual; "inspect" loading; "select" pick event type
  const [step, setStep] = useState("choose");
  const [apiKey, setApiKey] = useState("");
  const [account, setAccount] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [mode, setMode] = useState(settings?.default_mode || "video");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Manual fallback
  const [showManual, setShowManual] = useState(false);
  const [calLink, setCalLink] = useState(settings?.cal_link || "");

  const inspect = async (useOwnKey) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await inspectCalcom(useOwnKey ? apiKey : undefined);
      setAccount(data);
      setEventTypes(data.event_types || []);
      if (data.event_types?.length) setSelectedSlug(data.event_types[0].slug);
      setStep("select");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not connect to Cal.com.");
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    setBusy(true);
    setError(null);
    try {
      const ev = eventTypes.find((e) => e.slug === selectedSlug);
      await finalizeCalcom({
        api_key: account?.uses_platform_key ? undefined : apiKey,
        event_type_slug: selectedSlug,
        duration_min: ev?.length || 30,
        default_mode: mode,
      });
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const saveManual = async () => {
    setBusy(true);
    setError(null);
    try {
      await connectCalcom({ cal_link: calLink, default_mode: mode });
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save. Check your link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#FDFBF7] rounded-3xl shadow-2xl p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl">Connect Cal.com</h2>
          <button onClick={onClose} className="p-2 rounded-full border border-stone-200 hover:bg-stone-100 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* STEP: choose */}
        {step === "choose" && (
          <div className="space-y-4">
            <button
              onClick={() => inspect(false)}
              disabled={busy}
              className="w-full text-left rounded-2xl border border-stone-200 bg-white p-5 hover:border-[#C2410C] hover:shadow-sm transition-all disabled:opacity-60"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-[#C2410C] flex items-center justify-center shrink-0">
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                </div>
                <div>
                  <p className="font-medium text-stone-800">Use Wishire scheduling (recommended)</p>
                  <p className="text-sm text-stone-500">Instant setup — interviews are hosted on Wishire's Cal.com. No account needed.</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => { setShowManual(false); setStep("apikey"); }}
              className="w-full text-left rounded-2xl border border-stone-200 bg-white p-5 hover:border-stone-400 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-stone-100 text-stone-600 flex items-center justify-center shrink-0">
                  <Link2 size={18} />
                </div>
                <div>
                  <p className="font-medium text-stone-800">Connect my own Cal.com</p>
                  <p className="text-sm text-stone-500">Use your API key — interviews hosted on your account, webhook auto-configured.</p>
                </div>
              </div>
            </button>

            {error && <p className="text-red-600 text-sm">{error}</p>}
          </div>
        )}

        {/* STEP: api key entry */}
        {step === "apikey" && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Cal.com API key
              </label>
              <input
                type="password"
                placeholder="cal_live_..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="w-full px-4 py-2.5 rounded-2xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-stone-900 font-mono"
              />
              <p className="text-xs text-stone-500 mt-1.5">
                Get it from Cal.com → Settings → Developer → API Keys. We'll fetch your event types and set up the webhook automatically.
              </p>
            </div>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setStep("choose")} className="flex-1 px-4 py-3 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-100 text-sm transition-colors">
                Back
              </button>
              <button
                onClick={() => inspect(true)}
                disabled={busy || !apiKey.trim()}
                className="flex-1 px-4 py-3 rounded-full bg-stone-900 text-stone-50 hover:bg-stone-700 text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                {busy ? "Connecting…" : "Continue"}
              </button>
            </div>
          </div>
        )}

        {/* STEP: select event type */}
        {step === "select" && (
          <div className="space-y-5">
            {account && (
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 size={15} />
                Connected as <strong>{account.username}</strong>
                {account.uses_platform_key && <span className="text-emerald-600">(Wishire-hosted)</span>}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Which event type is your interview?
              </label>
              {eventTypes.length === 0 ? (
                <p className="text-sm text-stone-500">
                  No event types found. Create one on Cal.com first (e.g. a 30-min interview).
                </p>
              ) : (
                <div className="space-y-2">
                  {eventTypes.map((ev) => (
                    <button
                      key={ev.slug}
                      onClick={() => setSelectedSlug(ev.slug)}
                      className={`w-full text-left rounded-2xl border p-4 transition-all ${
                        selectedSlug === ev.slug
                          ? "border-[#C2410C] bg-amber-50"
                          : "border-stone-200 bg-white hover:border-stone-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-stone-800">{ev.title}</p>
                          <p className="text-xs text-stone-500 font-mono">/{ev.slug} · {ev.length} min</p>
                        </div>
                        {selectedSlug === ev.slug && <CheckCircle2 size={18} className="text-[#C2410C]" />}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Interview mode</label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                className="w-full px-4 py-2.5 rounded-2xl border border-stone-300 bg-white text-sm focus:outline-none"
              >
                <option value="video">Video</option>
                <option value="voice">Voice</option>
                <option value="onsite">On-site</option>
              </select>
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <div className="flex gap-3">
              <button onClick={() => setStep("choose")} className="flex-1 px-4 py-3 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-100 text-sm transition-colors">
                Back
              </button>
              <button
                onClick={finalize}
                disabled={busy || !selectedSlug}
                className="flex-1 px-4 py-3 rounded-full bg-stone-900 text-stone-50 hover:bg-stone-700 text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                {busy ? "Finishing…" : "Finish setup"}
              </button>
            </div>
          </div>
        )}

        {settings?.connected && step === "choose" && (
          <button
            onClick={onDisconnect}
            className="w-full mt-4 px-4 py-2.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-sm transition-colors"
          >
            Disconnect Cal.com
          </button>
        )}
      </div>
    </div>
  );
}
